import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "../../lib/medskin-data";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          {/* Left: Copy */}
          <div className="lg:col-span-7">
            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={0}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-10 bg-[#C4A981]"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]" data-testid="hero-eyebrow">
                Klinisk hudklinikk · Oslo
              </span>
            </motion.div>

            <motion.h1
              variants={fade}
              initial="hidden"
              animate="show"
              custom={1}
              className="font-serif text-[2.75rem] leading-[1.05] md:text-6xl lg:text-7xl text-[#2C2C2A] tracking-tight font-light"
              data-testid="hero-headline"
            >
              Der medisinsk <em className="italic text-[#4A4238]">kompetanse</em> møter helhetlig hudhelse.
            </motion.h1>

            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-8 max-w-xl text-base md:text-lg text-[#635D56] font-light leading-relaxed"
              data-testid="hero-subtext"
            >
              Vi jobber målrettet med årsakene bak hudutfordringer som inflammasjon, svekket hudbarriere
              og hormonell ubalanse – for å skape varige, synlige resultater.
            </motion.p>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="https://timma.no/salong/medskin-by"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-between gap-6 bg-[#4A4238] text-[#F7F6F3] hover:bg-[#2C2C2A] transition-all duration-500 rounded-full pl-7 pr-3 py-3 text-xs uppercase tracking-[0.22em]"
                data-testid="hero-book-btn"
              >
                Book konsultasjon
                <span className="rounded-full bg-[#C4A981] p-2.5 group-hover:rotate-45 transition-transform duration-500">
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </span>
              </a>
              <a
                href="#behandlinger"
                className="inline-flex items-center justify-center gap-2 border border-[#4A4238] text-[#4A4238] hover:bg-[#4A4238] hover:text-[#F7F6F3] transition-all duration-500 rounded-full px-7 py-4 text-xs uppercase tracking-[0.22em]"
                data-testid="hero-treatments-btn"
              >
                Se behandlinger
              </a>
            </motion.div>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-14 grid grid-cols-3 gap-6 max-w-md"
            >
              {[
                { k: "10+", v: "År erfaring" },
                { k: "2 400+", v: "Pasienter" },
                { k: "4.9★", v: "Vurdering" },
              ].map((s) => (
                <div key={s.v} className="border-l border-[#D1C9C0] pl-4">
                  <div className="font-serif text-2xl md:text-3xl text-[#2C2C2A]">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#635D56] mt-1">{s.v}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden">
              <img
                src={IMAGES.hero}
                alt="Sunn, glødende hud – Medskinby"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2A]/15 to-transparent"></div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#F7F6F3] border border-[#E5E0DA] rounded-2xl px-5 py-4 shadow-[0_20px_50px_-25px_rgba(74,66,56,0.35)]">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#635D56]">Sertifisert</div>
              <div className="font-serif text-lg text-[#2C2C2A] mt-1">Medisinsk hudklinikk</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative blob */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#EBE5DF] blur-3xl opacity-50"></div>
    </section>
  );
};
